import { useEffect, useRef } from 'react';
import { usePluginsReady } from '~/domain/plugins/plugins-ready';

/**
 * What `useTrackOnce` should do on a given render.
 *
 * `wait` exists only because the plugins are not ready yet: the timeout bounds
 * the wait for plugin init (the Amplitude download and `ampli.load()`) and
 * nothing else. Once we have tracked — or given up — there is no timer left.
 */
export type TrackOnceStep = 'track' | 'wait' | 'idle';

/**
 * The scheduling decision, extracted so it can be tested without a DOM.
 *
 * The bug this encodes against (US-12234): the timer used to be armed on every
 * effect run, including while `pluginsReady` was still false and no tracking
 * call had been made. The budget was therefore spent on SDK init rather than on
 * the tracking call it was meant to bound, and expired mid-flight.
 */
export function trackOnceStep({
  fired,
  pluginsReady,
  timeout,
}: {
  fired: boolean;
  pluginsReady: boolean;
  timeout?: number;
}): TrackOnceStep {
  if (fired) return 'idle';
  if (pluginsReady) return 'track';
  return timeout === undefined ? 'idle' : 'wait';
}

/**
 * Run a callback function exactly once in an effect. This is used for firing tracking events on component mount.
 * The callback is not fired until plugins (including ampli) have been initialized.
 * @param trackingCallback The function to run where the tracking call will be made (eg. `() => { ampli.track({}) })`)
 * @param dependencies All dependencies needed in the callback function
 * @param timeout An optional amount of milliseconds to wait for the plugins to initialize before giving up. This is useful in performance critical scenarios where proceeding is more important than tracking.
 * @param timeoutCallback An optional function that runs when the timeout is hit.
 */
export function useTrackOnce(
  trackingCallback: () => void,
  dependencies: unknown[],
  timeout?: number,
  timeoutCallback?: () => void
) {
  const fired = useRef(false);

  // Callers pass inline arrows, so these change identity on every render.
  // Holding them in refs keeps them out of the effect's dependency list: as
  // dependencies they re-run the effect on every render, and each run clears
  // and restarts the timer — so a component re-rendering faster than `timeout`
  // would never time out, while one that settled early would.
  const onTrack = useRef(trackingCallback);
  const onTimeout = useRef(timeoutCallback);
  useEffect(() => {
    onTrack.current = trackingCallback;
    onTimeout.current = timeoutCallback;
  });

  const pluginsReady = usePluginsReady();

  useEffect(() => {
    switch (trackOnceStep({ fired: fired.current, pluginsReady, timeout })) {
      case 'track':
        fired.current = true;
        onTrack.current();
        return;

      case 'wait': {
        const timeoutId = setTimeout(() => {
          if (fired.current) return;
          fired.current = true;
          onTimeout.current?.();
        }, timeout);
        return () => clearTimeout(timeoutId);
      }

      case 'idle':
        return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pluginsReady, timeout, ...dependencies]);
}
